import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import Modal from "react-modal";
import { Search, Download, X } from "lucide-react";
import SupplierDetail from "./SupplierDetail";
import ImportSuppliersModal from "../../components/ImportSuppliersModal";
import SupplierTypeManagementModal from "../../components/SupplierTypeManagementModal";
import LoadingModal from "../../components/LoadingModal";
import { adminServices } from "../../services/AdminServices";
import { toast } from "react-toastify";
import axios from "axios";

const SupplierManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [supplierTypeFilter, setSupplierTypeFilter] = useState("All");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateSuppliers, setDuplicateSuppliers] = useState([]);
  const [pendingImportData, setPendingImportData] = useState(null);
  const [suppliersData, setSuppliersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
  const [isSupplierTypeModalOpen, setIsSupplierTypeModalOpen] = useState(false);
  const [supplierTypes, setSupplierTypes] = useState([]);
  const [supplierErrors, setSupplierErrors] = useState({});
  const [newSupplier, setNewSupplier] = useState({
    typeId: 0,
    supplierName: "",
    contactName: "",
    email: "",
    address: "",
    city: "",
    region: "",
    paymentMethod: "",
    contactNo: "",
    faxNo: "",
    fulfillmentRate: "",
    shipmentPeriod: "",
    status: ""
  });

  // Fetch suppliers from API
  useEffect(() => {
    fetchSuppliers();
    fetchSupplierTypes();
  }, []);

  const mapSupplierTypeData = (types) => {
    return (Array.isArray(types) ? types : [])
      .map((item, index) => ({
        id: Number(item?.id) || index + 1,
        name: String(item?.name || "").trim(),
        status: String(item?.status || "").trim()
      }))
      .filter((item) => item.name);
  };

  const fetchSupplierTypes = async () => {
    try {
      const response = await adminServices.storeProcedure2('getSupplierTypes',{});
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

      const mapped = mapSupplierTypeData(data);
      if (mapped.length > 0) {
        setSupplierTypes(mapped);
      }
    } catch (error) {
      console.error("Error fetching supplier types:", error);
    }
  };

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await adminServices.getData('Suppliers');
      if (response && response.data) {
        const rawSuppliers = Array.isArray(response.data) ? response.data : [];

        // Keep supplier type options available even when SupplierTypes endpoint is not yet configured.
        if (!supplierTypes.length) {
          const typeMap = new Map();
          rawSuppliers.forEach((supplier) => {
            const t = supplier.type;
            if (t && t.id && t.name) {
              typeMap.set(Number(t.id), String(t.name));
            }
          });
          setSupplierTypes((prev) => {
            if (prev.length) return prev;
            return Array.from(typeMap.entries()).map(([id, name]) => ({ id, name, status: "Active" }));
          });
        }

        // Map API response to table format - include ALL suppliers (Active, Pending, Inactive)
        const mappedData = rawSuppliers
          .map((supplier, index) => {
              // Normalize status - handle both numeric and string formats
              let normalizedStatus = "-";
              if (supplier.status === 1 || supplier.status === "Active" || supplier.status === "active") {
                normalizedStatus = "Active";
              } else if (supplier.status === 0 || supplier.status === "Inactive" || supplier.status === "inactive") {
                normalizedStatus = "Inactive";
              } else if (supplier.status === "Pending" || supplier.status === "pending") {
                normalizedStatus = "Pending";
              } else if (supplier.status) {
                // Keep other status values as-is (capitalize first letter)
                normalizedStatus = typeof supplier.status === 'string' 
                  ? supplier.status.charAt(0).toUpperCase() + supplier.status.slice(1).toLowerCase()
                  : supplier.status;
              }

              return {
                id: supplier.id || index + 1,
                supplierId: supplier.supplierId || supplier.supplier_id || supplier.id || "-",
                supplierName: supplier.supplierName || supplier.supplier_name || "-",
                supplierType: supplier.type?.name || supplier.typeName || supplier.type_name || "-",
                status: normalizedStatus,
                categories: supplier.categories || supplier.category || [],
                actions: "View",
                contactPerson: supplier.contactName || supplier.contactPerson || supplier.contact_name || "-",
                phone: supplier.contactNo || supplier.phone || supplier.contact_no || supplier.phoneNumber || "-",
                email: supplier.email || "-",
                paymentMethod: supplier.paymentMethod || supplier.payment_method || "-",
                address: supplier.address || "-",
                shipmentPeriod: supplier.shipmentPeriod || supplier.shipment_period || "-",
                businessCategory: supplier.businessCategory || supplier.business_category || "-",
                fulfillmentRate: supplier.fulfillmentRate || supplier.fulfillment_rate || "-",
                standardShipmentPeriod: supplier.standardShipmentPeriod || supplier.standard_shipment_period || "-",
                originalData: supplier // Keep original data for detail view
              };
            });
        
        // Log counts for debugging
        const activeCount = mappedData.filter(s => s.status === "Active").length;
        const pendingCount = mappedData.filter(s => s.status === "Pending").length;
        const inactiveCount = mappedData.filter(s => s.status === "Inactive").length;
        //console.log(`Fetched suppliers: ${mappedData.length} total (Active: ${activeCount}, Pending: ${pendingCount}, Inactive: ${inactiveCount})`);
        
        setSuppliersData(mappedData);
        toast.success("Suppliers loaded successfully");
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      // Keep empty array on error
      setSuppliersData([]);
    } finally {
      setLoading(false);
    }
  };

  // Download Template handler
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL || ''}supplier-import/download-template`,
        {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('appToken'),
          },
          responseType: 'blob'
        }
      );
      // Handle file download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'supplier-master-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
      alert("Failed to download template. Please try again.");
    }
  };

  // Import handler
  const handleImport = async (data) => {
    try {
      const formData = new FormData();
      formData.append('file', data.file);

      // Validate first
      const validateResponse = await axios.post(
        `${process.env.REACT_APP_BASE_URL || ''}supplier-import/validate`,
        formData,
        {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('appToken'),
          },
        }
      );
      
      // Check for duplicates
      if (validateResponse.data && validateResponse.data.hasDuplicates) {
        // Show duplicate modal
        setDuplicateSuppliers(validateResponse.data.duplicatedSuppliers || []);
        setPendingImportData(data);
        setShowDuplicateModal(true);
        setIsImportModalOpen(false);
        return;
      }
      
      // If no duplicates, proceed with import
      await proceedWithImport(data, formData);
    } catch (error) {
      console.error("Error importing suppliers:", error);
      alert(error.response?.data?.message || "Failed to import suppliers. Please try again.");
    }
  };

  // Proceed with import after validation
  const proceedWithImport = async (data, formData) => {
    try {
      const importResponse = await axios.post(
        `${process.env.REACT_APP_BASE_URL || ''}supplier-import`,
        formData,
        {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('appToken'),
          },
        }
      );
      
      //console.log("Import successful:", importResponse);
      
      // Handle response structure
      if (importResponse.data && importResponse.data.success) {
        const { message, totalRecords, successfulRecords, failedRecords } = importResponse.data;
        
        let successMessage = message || "Suppliers imported successfully!";
        if (totalRecords !== undefined) {
          successMessage += ` (${successfulRecords}/${totalRecords} records)`;
          if (failedRecords > 0) {
            successMessage += `, ${failedRecords} failed`;
          }
        }
        
        toast.success(successMessage);
      } else {
        toast.success("Suppliers imported successfully!");
      }
      
      setIsImportModalOpen(false);
      setShowDuplicateModal(false);
      setPendingImportData(null);
      // Refresh the suppliers list after successful import
      fetchSuppliers();
    } catch (error) {
      console.error("Error importing suppliers:", error);
      alert(error.response?.data?.message || "Failed to import suppliers. Please try again.");
    }
  };

  // Fetch supplier by ID when View is clicked, then show SupplierDetail
  const handleViewSupplier = async (row) => {
    const supplierId = row.originalData?.id ?? row.id ?? row.supplierId;
    if (!supplierId || supplierId === "-") {
      toast.error("Invalid supplier. Cannot load details.");
      return;
    }
    setLoadingDetail(true);
    try {
      const response = await adminServices.getData(`Suppliers/${supplierId}`);
      if (response && response.data) {
        const data = response.data;
        const normalizedStatus = data.status === 1 || data.status === "Active" || data.status === "active"
          ? "Active"
          : data.status === 0 || data.status === "Inactive" || data.status === "inactive"
            ? "Inactive"
            : data.status === "Pending" || data.status === "pending"
              ? "Pending"
              : typeof data.status === "string" ? data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase() : "-";
        const mappedSupplier = {
          id: data.id,
          supplierId: data.id ?? data.supplierId ?? "-",
          supplierName: data.supplierName || data.supplier_name || "-",
          status: normalizedStatus,
          categories: data.categories || data.category || [],
          contactPerson: data.contactName || data.contactPerson || data.contact_name || "-",
          phone: data.contactNo || data.phone || data.contact_no || data.phoneNumber || "-",
          email: data.email || "-",
          paymentMethod: data.paymentMethod || data.payment_method || "-",
          address: data.address || "-",
          shipmentPeriod: data.shipmentPeriod || data.shipment_period || "-",
          businessCategory: data.businessCategory || data.business_category || "-",
          fulfillmentRate: data.fulfillmentRate || data.fulfillment_rate || "-",
          standardShipmentPeriod: data.standardShipmentPeriod || data.standard_shipment_period || "-",
          originalData: data
        };
        setSelectedSupplier(mappedSupplier);
      } else {
        toast.error("Failed to load supplier details.");
      }
    } catch (error) {
      console.error("Error fetching supplier details:", error);
      toast.error(error.response?.data?.message || "Failed to load supplier details. Please try again.");
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle proceed with duplicates
  const handleProceedWithDuplicates = async () => {
    if (pendingImportData) {
      const formData = new FormData();
      formData.append('file', pendingImportData.file);
      await proceedWithImport(pendingImportData, formData);
    }
  };

  // Filter data based on search
  const supplierTypeOptions = useMemo(() => {
    const set = new Set();
    (suppliersData || []).forEach((s) => {
      const t = String(s?.supplierType ?? "").trim();
      if (t && t !== "-") set.add(t);
    });
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [suppliersData]);

  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const type = String(supplierTypeFilter || "All");

    return (suppliersData || []).filter((supplier) => {
      if (type !== "All" && String(supplier?.supplierType ?? "-") !== type) return false;

      if (!query) return true;
      const searchableFields = [
        supplier.supplierId,
        supplier.supplierName,
        supplier.supplierType,
        supplier.contactPerson,
        supplier.email,
        supplier.phone,
        supplier.status
      ];
      return searchableFields.some((field) => field && field.toString().toLowerCase().includes(query));
    });
  }, [suppliersData, searchQuery, supplierTypeFilter]);

  const columns = [
    {
      name: "Supplier ID",
      selector: row => row.supplierId || "-",
      sortable: true,
      width: "20%"
    },
    {
      name: "Supplier Name",
      selector: row => row.supplierName || "-",
      sortable: true,
      width: "20%"
    },
    {
      name: "Status",
      cell: row => {
        if (!row.status || row.status === "-") return <div></div>;
        // Determine badge class based on status
        let badgeClass = "inactive"; // default
        if (row.status === "Active") {
          badgeClass = "active";
        } else if (row.status === "Pending") {
          badgeClass = "pending";
        } else if (row.status === "Inactive") {
          badgeClass = "inactive";
        }
        
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <span
              className={`supplier-status-badge ${badgeClass}`}
            >
              {row.status}
            </span>
          </div>
        );
      },
      sortable: true,
      width: "20%",
      center: true
    },
    {
      name: "Supplier Type",
      cell: row => {
        const t = row.supplierType || "-";
        if (!t || t === "-") return <div></div>;
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
            <span className="supplier-category-badge">{t}</span>
          </div>
        );
      },
      sortable: true,
      width: "20%",
      center: true
    },
    {
      name: "Actions",
      cell: row => {
        if (!row.actions) return <div></div>;
        return (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <button 
              className="supplier-view-link"
              onClick={(e) => {
                e.stopPropagation();
                handleViewSupplier(row);
              }}
              disabled={loadingDetail}
            >
              {row.actions}
            </button>
          </div>
        );
      },
      sortable: false,
      width: "20%",
      center: true
    }
  ];

  const customStyles = {
    table: {
      style: {
        tableLayout: 'fixed'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#0F2A40",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "center"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }
    }
  };

  if (selectedSupplier) {
    return (
      <>
        <LoadingModal show={loadingDetail} message="Loading supplier details..." />
        <SupplierDetail 
        supplier={selectedSupplier} 
        onBack={() => {
          setSelectedSupplier(null);
          // Refresh supplier list when returning from detail page
          fetchSuppliers();
        }} 
      />
      </>
    );
  }

  return (
    <div className="dashboard-content">
      <LoadingModal show={loading} message="Loading Suppliers..." />
      <div className="supplier-management-page">
        <div className="supplier-management-header">
          <h1 className="dashboard-title">Supplier Management</h1>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              className="supplier-import-btn"
              onClick={() => setIsAddSupplierOpen(true)}
              style={{
                backgroundColor: "#ffffff",
                color: "#000000",
                borderColor: "#000000",
                borderWidth: 1,
                borderStyle: "solid"
              }}
            >
              + Add Supplier
            </button>
            <button
              className="supplier-import-btn"
              onClick={() => setIsSupplierTypeModalOpen(true)}
              style={{
                backgroundColor: "#ffffff",
                color: "#000000",
                borderColor: "#000000",
                borderWidth: 1,
                borderStyle: "solid"
              }}
            >
              + Add Supplier Type
            </button>
            <button 
              className="supplier-import-btn"
              onClick={handleDownloadTemplate}
            >
              <Download size={18} />
              Download Template
            </button>
            <button 
              className="supplier-import-btn"
              onClick={() => setIsImportModalOpen(true)}
            >
              <Download size={18} />
              Import CSV
            </button>
          </div>
        </div>

      <div
        className="supplier-management-search"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap"
        }}
      >
        <div className="supplier-search-container" style={{ flex: 1, minWidth: 260 }}>
          <Search className="supplier-search-icon" size={18} />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="supplier-search-input"
          />
        </div>

        <select
          value={supplierTypeFilter}
          onChange={(e) => setSupplierTypeFilter(e.target.value)}
          className="merchant-filter-select"
          style={{ minWidth: 220 }}
        >
          <option value="All">All</option>
          {supplierTypes.map((opt) => (
            <option key={opt.id} value={opt.name}>
              {opt.name}
            </option>
          ))}
        </select>
      </div>

        <div className="supplier-management-table-section">
          <div className="supplier-table-container">
            <DataTable
              columns={columns}
              data={filteredData}
              customStyles={customStyles}
              striped
              highlightOnHover
              noHeader={false}
              pagination={false}
            />
          </div>
        </div>

        {/* Import Suppliers Modal */}
        <ImportSuppliersModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImport}
        />

        <SupplierTypeManagementModal
          isOpen={isSupplierTypeModalOpen}
          onClose={() => setIsSupplierTypeModalOpen(false)}
          onUpdated={fetchSupplierTypes}
        />

        {/* Add Supplier Modal */}
        {isAddSupplierOpen && (
          <div
            className="jmodal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsAddSupplierOpen(false);
              }
            }}
          >
            <div
              className="jmodal-content"
              style={{
                minWidth: 720,
                maxWidth: 880,
                width: "90%",
                maxHeight: "90vh",
                overflowY: "auto",
                textAlign: "left",
                borderRadius: 24,
                padding: 32
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  marginBottom: 24,
                  textAlign: "center",
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "#111827"
                }}
              >
                Add Supplier
              </h2>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 24
                }}
              >
                <div style={{ gridColumn: "1 / span 2" }}>
                  {/* <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Supplier ID
                  </label>
                  <input
                    type="text"
                    disabled
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#f3f4f6",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                  /> */}
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Supplier Name
                  </label>
                  <input
                    type="text"
                    value={newSupplier.supplierName}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, supplierName: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.supplierName ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.supplierName && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Supplier Type
                  </label>
                  <select
                    value={newSupplier.typeId}
                    onChange={(e) =>
                      setNewSupplier((s) => ({ ...s, typeId: e.target.value ? Number(e.target.value) : 0 }))
                    }
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.typeId ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14,
                      backgroundColor: "#ffffff"
                    }}
                    required
                  >
                    <option value={0}>Select type</option>
                    {supplierTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {supplierErrors.typeId && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={newSupplier.contactName}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, contactName: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.contactName ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.contactName && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={newSupplier.email}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, email: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.email ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.email && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    value={newSupplier.contactNo}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, contactNo: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.contactNo ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.contactNo && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>

                <div style={{ gridColumn: "1 / span 2" }}>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Address
                  </label>
                  <input
                    type="text"
                    value={newSupplier.address}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, address: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.address ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.address && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={newSupplier.city}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, city: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.city ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.city && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Region
                  </label>
                  <input
                    type="text"
                    value={newSupplier.region}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, region: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.region ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.region && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Payment Method
                  </label>
                  <input
                    type="text"
                    value={newSupplier.paymentMethod}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, paymentMethod: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.paymentMethod ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.paymentMethod && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Shipping Period
                  </label>
                  <input
                    type="text"
                    value={newSupplier.shipmentPeriod}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, shipmentPeriod: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.shipmentPeriod ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.shipmentPeriod && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Fax No
                  </label>
                  <input
                    type="text"
                    value={newSupplier.faxNo}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, faxNo: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.faxNo ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.faxNo && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Fulfillment Rate
                  </label>
                  <input
                    type="text"
                    value={newSupplier.fulfillmentRate}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, fulfillmentRate: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.fulfillmentRate ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14
                    }}
                    required
                  />
                  {supplierErrors.fulfillmentRate && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                    Status
                  </label>
                  <select
                    value={newSupplier.status}
                    onChange={(e) => setNewSupplier((s) => ({ ...s, status: e.target.value }))}
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 8,
                      border: supplierErrors.status ? "1px solid #dc2626" : "1px solid #d1d5db",
                      padding: "0 12px",
                      fontSize: 14,
                      backgroundColor: "#ffffff"
                    }}
                    required
                  >
                    <option value="">Select status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  {supplierErrors.status && (
                    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>
                      This field is required
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  marginTop: 8
                }}
              >
                <button
                  type="button"
                  className="modalButton2"
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#ffffff",
                    color: "#002347",
                    borderColor: "#000000",
                    borderWidth: 1,
                    borderStyle: "solid"
                  }}
                  onClick={() => setIsAddSupplierOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="modalButton1"
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#002347"
                  }}
                  onClick={async () => {
                    try {
                      // simple front-end required validation with per-field errors
                      const errors = {};
                      if (!newSupplier.supplierName.trim()) errors.supplierName = true;
                      if (!newSupplier.typeId) errors.typeId = true;
                      if (!newSupplier.contactName.trim()) errors.contactName = true;
                      if (!newSupplier.email.trim()) errors.email = true;
                      if (!newSupplier.address.trim()) errors.address = true;
                      if (!newSupplier.city.trim()) errors.city = true;
                      if (!newSupplier.region.trim()) errors.region = true;
                      if (!newSupplier.paymentMethod.trim()) errors.paymentMethod = true;
                      if (!newSupplier.contactNo.trim()) errors.contactNo = true;
                      if (!newSupplier.faxNo.trim()) errors.faxNo = true;
                      if (!newSupplier.fulfillmentRate.trim()) errors.fulfillmentRate = true;
                      if (!newSupplier.shipmentPeriod.trim()) errors.shipmentPeriod = true;
                      if (!newSupplier.status.trim()) errors.status = true;

                      if (Object.keys(errors).length > 0) {
                        setSupplierErrors(errors);
                        return;
                      }
                      setSupplierErrors({});
                      const payload = {
                        typeId: Number(newSupplier.typeId) || 0,
                        supplierName: newSupplier.supplierName,
                        contactName: newSupplier.contactName,
                        email: newSupplier.email,
                        address: newSupplier.address,
                        city: newSupplier.city,
                        region: newSupplier.region,
                        paymentMethod: newSupplier.paymentMethod,
                        contactNo: newSupplier.contactNo,
                        faxNo: newSupplier.faxNo,
                        fulfillmentRate: newSupplier.fulfillmentRate,
                        shipmentPeriod: newSupplier.shipmentPeriod,
                        status: newSupplier.status
                      };
                      await adminServices.createSupplier(payload);
                      setIsAddSupplierOpen(false);
                      setNewSupplier({
                        typeId: 0,
                        supplierName: "",
                        contactName: "",
                        email: "",
                        address: "",
                        city: "",
                        region: "",
                        paymentMethod: "",
                        contactNo: "",
                        faxNo: "",
                        fulfillmentRate: "",
                        shipmentPeriod: "",
                        status: ""
                      });
                      fetchSuppliers();
                      toast.success("Supplier created successfully.");
                    } catch (error) {
                      console.error("Failed to create supplier:", error);
                      toast.error(error.response?.data?.message || "Failed to create supplier.");
                    }
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Suppliers Modal */}
        <Modal
          isOpen={showDuplicateModal}
          onRequestClose={() => setShowDuplicateModal(false)}
          className="import-products-modal"
          overlayClassName="import-products-modal-overlay"
          ariaHideApp={false}
        >
          <div className="import-products-modal-content">
            <button 
              className="import-products-close-btn-top" 
              onClick={() => setShowDuplicateModal(false)}
            >
              <X size={20} />
            </button>
            
            <div className="import-products-body">
              <h3 className="import-products-card-title">Duplicate Suppliers Found</h3>
              
              <div className="import-products-form-card">
                <p className="import-products-confirm-text" style={{ marginBottom: '16px' }}>
                  {duplicateSuppliers.length > 0 
                    ? `Found ${duplicateSuppliers.length} duplicate supplier(s). Please review the list below.`
                    : 'Duplicate suppliers were found in your file.'}
                </p>

                <div className="duplicate-products-list-container" style={{ 
                  maxHeight: '400px', 
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '20px'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Supplier ID</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Supplier Name</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Email</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {duplicateSuppliers.map((supplier, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px', fontSize: '0.875rem' }}>{supplier.supplierId || supplier.supplier_id}</td>
                          <td style={{ padding: '8px', fontSize: '0.875rem' }}>{supplier.supplierName || supplier.supplier_name}</td>
                          <td style={{ padding: '8px', fontSize: '0.875rem' }}>{supplier.email}</td>
                          <td style={{ padding: '8px', fontSize: '0.875rem', color: '#6b7280' }}>{supplier.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="import-products-confirm-messages">
                  <p className="import-products-confirm-text">
                    Do you want to proceed with the import? This will update existing suppliers with matching Supplier IDs.
                  </p>
                </div>

                {/* Buttons */}
                <div className="import-products-confirm-actions">
                  <button 
                    className="import-products-proceed-btn" 
                    onClick={handleProceedWithDuplicates}
                  >
                    Proceed Anyway
                  </button>
                  <button 
                    className="import-products-cancel-confirm-btn" 
                    onClick={() => setShowDuplicateModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default SupplierManagement;

